import React, { useState, useEffect } from 'react'
import Carousel from 'components/modular/carousel'
import EnglishLevelItem from './english-level-item/EnglishLevelItem'
import { LEVEL_SEARCH_PAGE } from 'routes/paths'
import { post_event } from 'utils/analytics'
import Shelf from 'components/modular/shelves/shelf'
import {
  buttonTextOptions,
  carouselEnglishLevelSettings,
  CEFR_TITLE,
  FOUNTAS_PINNEL_TITLE,
  titleOptions
} from './EnglishLevel.constants'
import { useHistory } from 'react-router-dom'
import {
  EnglishLevelWrapper,
  StyledEnglishLevelHeader,
  StyledTitle
} from './EnglishLevel.styles'
import { tokens } from '@arvoreeducacao/bonsai-ds'
import { Module } from 'api/data.types'
import { CEFR_SUB_TITLES } from 'utils/englishLevels'
import type { LanguageTokens } from 'i18n'
import { useTranslation } from 'i18n'
import RunningRecordsBanner from 'components/running-records/RunningRecordsBanner'
import ClassificationModal from './classification-modal'
import { useUserInfo } from 'utils/hooks/use-userInfo'
import RunningRecordsBookShelf from '../home/running-records-book-shelf'
import { useApolloClient } from '@apollo/client'
import { getReaderEnglishLevels } from 'api/queries/reader'
import type { GetReaderEnglishLevelsQuery } from '__gql-generated__/types'

const EnglishLevelCarousel: React.FC = () => {
  const history = useHistory()
  const client = useApolloClient()
  const [fountasAndPinnellLevels, setFountasAndPinnelLevels] = useState([])
  const [cefrLevels, setCefrLevels] = useState([])
  const [showEnglishLevelModal, setShowEnglishLevelModal] = useState(false)
  const [title, setTitle] = useState<string>(titleOptions.desktop)
  const [buttonText, setButtonText] = useState<string>(
    buttonTextOptions.desktop
  )

  const { user } = useUserInfo()

  const handleClick = value => {
    post_event('click english level/ENGLISH', value)
    history.push({
      pathname: LEVEL_SEARCH_PAGE(`Nível ${value}`),
      search: `english_level=${value}`
    })
  }

  const handleResize = () => {
    if (window.innerWidth >= tokens.sizes.mdScreen) {
      setTitle(titleOptions.desktop)
      setButtonText(buttonTextOptions.desktop)
    } else {
      setTitle(titleOptions.mobile)
      setButtonText(buttonTextOptions.mobile)
    }
  }

  const handleEnglishLevels = async () => {
    client
      .query<GetReaderEnglishLevelsQuery>({
        query: getReaderEnglishLevels
      })
      .then(response => {
        const { listEnglishLevels } = response?.data?.me?.reader || {}

        const fountasAndPinnell = listEnglishLevels.find(
          level => level.type === 'FOUNTAS_AND_PINNELL'
        )
        const cefr = listEnglishLevels.find(level => level.type === 'CEFR')

        const filteredFountasAndPinnell = fountasAndPinnell.levels.map(
          level => {
            return {
              label: level
            }
          }
        )

        const filteredCefr = cefr.levels.map(level => {
          return {
            label: level,
            info: CEFR_SUB_TITLES[level] || ''
          }
        })

        setFountasAndPinnelLevels(filteredFountasAndPinnell)
        setCefrLevels(filteredCefr)
      })
      .catch(error => {
        console.error(error)
      })
  }

  useEffect(() => {
    handleEnglishLevels()
    handleResize()
    window.addEventListener('resize', handleResize, false)

    return () => {
      window.removeEventListener('resize', handleResize, false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const { t } = useTranslation()

  const closeEnglishModal = () => setShowEnglishLevelModal(false)

  const openEnglishModal = () => {
    setShowEnglishLevelModal(true)
  }

  return (
    <EnglishLevelWrapper>
      <Shelf
        title={t(title as LanguageTokens)}
        isSeeAllButtonVisible={false}
        infoButton
        infoButtonFunction={openEnglishModal}
        infoButtonText={t(buttonText as LanguageTokens)}
      >
        <StyledEnglishLevelHeader>
          {showEnglishLevelModal && (
            <ClassificationModal
              show={showEnglishLevelModal}
              onClose={closeEnglishModal}
            />
          )}
          <StyledTitle size={4}>{t(CEFR_TITLE as LanguageTokens)}</StyledTitle>
        </StyledEnglishLevelHeader>
        <Carousel
          settings={carouselEnglishLevelSettings({ isCEFR: true })}
          spaceBetweenItems={tokens.spacing.horizontalMedium}
          contentFilter={Module.INGLES}
        >
          {cefrLevels?.map(value => (
            <EnglishLevelItem
              key={value.label}
              level={value.label}
              subText={t(value.info)}
              onClick={() => handleClick(value.label)}
              isCEFR={true}
            />
          ))}
        </Carousel>
        <StyledEnglishLevelHeader>
          <StyledTitle size={4}>{t(FOUNTAS_PINNEL_TITLE)}</StyledTitle>
        </StyledEnglishLevelHeader>
        <Carousel
          settings={carouselEnglishLevelSettings({ isCEFR: false })}
          spaceBetweenItems={tokens.spacing.horizontalMedium}
          contentFilter={Module.INGLES}
        >
          {fountasAndPinnellLevels?.map(value => (
            <EnglishLevelItem
              key={value.label}
              level={value.label}
              onClick={() => handleClick(value.label)}
            />
          ))}
        </Carousel>

        <RunningRecordsBanner user={user} />
      </Shelf>

      <RunningRecordsBookShelf />
    </EnglishLevelWrapper>
  )
}

export default EnglishLevelCarousel
